// Ficha 360° del paciente: cabecera demográfica con foto + secciones clínicas
// (antecedentes, consultas, recetas, pedidos de laboratorio, certificados,
// citas, vacunas, signos vitales y documentos). Se abre desde el listado de
// Pacientes; el lápiz de la cabecera lleva al formulario de edición.
import { Fragment, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, BadgeDollarSign, BookOpen, Camera, ChevronDown, ChevronRight, ClipboardList,
  FileText, FlaskConical, FolderOpen, HeartPulse, LayoutDashboard, Loader2, Microscope, Pencil, Pill, Scissors, Syringe, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { GENEROS, calcEdad, medRpc } from './shared';
import { AntecedentesGenerales, AntecedentesPediatricos, resumenAntecedente } from './Antecedentes';
import type { AntecedenteRow, AntecedentesData } from './Antecedentes';
import { EsquemaVacunacion } from './EsquemaVacunacion';
import { EnfermedadActual } from './EnfermedadActual';
import { ExamenesEspecialidad, ExamenesImagen, ExamenesLaboratorio, ExamenesPatologia, FormulariosPaciente, VisorArchivo } from './ExamenesFicha';
import { ReclamoAseguradora } from './ReclamoAseguradora';
import {
  ArchivosAdjuntos, CertificadosFicha, ChequeoPreoperatorio, ConsentimientoInformado,
  CobrosFicha, InformesFicha, InterconsultaFicha, OrdenIngreso, PedidosFicha, ProcedimientosFicha, ProtocoloOperatorio, RecetasFicha,
} from './DocumentosFicha';
import { HistoriaClinicaPaciente } from './HistoriaClinica';
import { LOCALE } from '@/lib/utils/regional';

interface Pac {
  id: string; codigo: string | null; fecha_nacimiento: string | null; genero: string | null;
  tipo_sangre: string | null; alergias: string | null; antecedentes: string | null;
  medicamentos_actuales: string | null; observaciones: string | null;
  aseguradora: string | null; numero_poliza: string | null; fecha_registro: string | null;
  foto_url: string | null; es_activo: boolean;
}
interface Par {
  razon_social: string; identificacion: string | null; email: string | null;
  telefono: string | null; celular: string | null; nacionalidad: string | null;
  ciudad: string | null; pais: string | null; direccion: string | null;
}
interface Documento { id: string; tipo: string | null; nombre: string; archivo_url: string | null; fecha: string | null }
interface Detalle { paciente: Pac; partner: Par; documentos: Documento[]; centro_costo: { codigo: string | null; nombre: string } | null }

interface Evolucion {
  evolucion: { id: string; fecha: string; motivo: string | null; firmada: boolean; plan?: string | null; episodio_id?: string | null };
  medico: string; especialidad: string | null;
  diagnosticos: { cie10_codigo: string | null; descripcion: string | null; es_principal: boolean }[];
  signos: Signo[];
}
interface Signo {
  fecha: string; presion_sistolica: number | null; presion_diastolica: number | null;
  frecuencia_cardiaca: number | null; frecuencia_resp: number | null; temperatura: number | null;
  spo2: number | null; peso: number | null; talla: number | null; glucosa: number | null;
}
interface Vacuna { id: string; vacuna: string; dosis: string | null; fecha: string; via: string | null; proxima_fecha: string | null }
interface Historia { vacunas: Vacuna[]; evoluciones: Evolucion[] }

interface ItemRecetaDet {
  medicamento: string; comercial: string | null; concentracion: string | null;
  via: string | null; forma: string | null; cantidad: string | null; indicaciones: string | null;
}
interface Receta {
  id: string; codigo: string | null; fecha: string; estado: string; diagnostico: string | null;
  renovable: boolean; refills_total: number | null; refills_usados: number | null; medico: string; items: number;
  datos?: Record<string, unknown> | null; items_detalle?: ItemRecetaDet[];
}
// Registros de la ficha (examen_registro): pedidos y procedimientos del resumen
interface RegFicha { id: string; categoria: string; numero: number | null; fecha: string; datos: Record<string, unknown> }
interface Certificado {
  id: string; codigo: string | null; fecha: string; tipo: string | null; estado: string;
  diagnostico: string | null; dias_reposo: number | null; medico: string;
}
interface Cita {
  id: string; fecha: string; hora_inicio: string | null; hora_fin: string | null; estado: string;
  tipo_atencion: string | null; consultorio: string | null; motivo: string | null;
  medico: string; especialidad: string | null;
}
interface Citas { proxima: Cita | null; citas: Cita[] }

// Ítems del menú que abren gestor propio (los atajos de scroll se retiraron
// el 2026-07-21; las secciones siguen en el Resumen Clínico vía scroll)
const SECCIONES = [
  ['recetas', 'Recetas', Pill],
  ['pedidos', 'Pedidos', FlaskConical],
  ['vacunas', 'Vacunas', Syringe],
] as const;

const fmtF = (v?: string | null) => {
  if (!v) return '—';
  const d = v.includes('T') ? new Date(v) : new Date(`${v}T00:00:00`);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(LOCALE, { day: '2-digit', month: 'short', year: 'numeric' });
};
const hora = (h?: string | null) => (h ? h.slice(0, 5) : '—');
const iniciales = (nombre: string) =>
  nombre.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';

function EstadoBadge({ v }: { v: string }) {
  const cls: Record<string, string> = {
    activa: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    emitido: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    entregada: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    atendida: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    anulada: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    cancelada: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    no_asistio: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  };
  return (
    <span className={`inline-block rounded px-1.5 py-0.5 text-xs capitalize ${cls[v] ?? 'bg-muted text-muted-foreground'}`}>
      {v.replace(/_/g, ' ')}
    </span>
  );
}

function Seccion({ titulo, innerRef, children }: { titulo: string; innerRef: (el: HTMLElement | null) => void; children: React.ReactNode }) {
  return (
    <section ref={innerRef} className="scroll-mt-4">
      <div className="rounded-t-md bg-primary/10 px-4 py-2.5 text-sm font-semibold">{titulo}</div>
      <div className="rounded-b-md border border-t-0 bg-card px-4 py-3 overflow-x-auto">{children}</div>
    </section>
  );
}

const Vacio = ({ texto }: { texto: string }) => <p className="py-1 text-sm text-primary/80">{texto}</p>;

const TH = ({ children }: { children: React.ReactNode }) => <th className="py-2 pr-3 text-left text-xs font-medium text-muted-foreground">{children}</th>;
const TD = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => <td className={`py-2 pr-3 align-top ${className}`}>{children}</td>;

export function FichaPaciente({ pacienteId, onVolver, onEditar }: { pacienteId: string; onVolver: () => void; onEditar: (id: string) => void }) {
  const qc = useQueryClient();
  const refs = useRef<Record<string, HTMLElement | null>>({});
  const ir = (k: string) => refs.current[k]?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  // 'ficha' | antecedentes (generales/gineco/pediátricos) | vacunas | enfermedad actual | exámenes (lab/img/esp/pat)
  const [docVisto, setDocVisto] = useState<string | null>(null);
  const [vista, setVista] = useState<'ficha' | 'general' | 'gineco' | 'pediatrico' | 'vacunas' | 'enfermedad' | 'ex_lab' | 'ex_img' | 'ex_esp' | 'ex_pat' | 'formularios'
    | 'doc_adj' | 'doc_inf' | 'doc_con' | 'doc_ord' | 'doc_cert' | 'doc_int' | 'doc_pre' | 'doc_pro' | 'procedimientos' | 'recetas_ficha' | 'pedidos_ficha' | 'cobros' | 'historia' | 'reclamos'>('ficha');
  const [menuAnt, setMenuAnt] = useState(false);
  const [menuEx, setMenuEx] = useState(false);
  const [menuDoc, setMenuDoc] = useState(false);
  const irSeccion = (k: string) => {
    if (vista !== 'ficha') { setVista('ficha'); setTimeout(() => ir(k), 80); } else ir(k);
  };

  const { data: det, isLoading } = useQuery<Detalle>({ queryKey: ['med-ficha', pacienteId, 'detalle'], queryFn: () => medRpc<Detalle>('med_paciente', { _id: pacienteId }) });
  const { data: his } = useQuery<Historia>({ queryKey: ['med-ficha', pacienteId, 'historia'], queryFn: () => medRpc<Historia>('med_historia', { _paciente_id: pacienteId }) });
  const { data: recetas = [] } = useQuery<Receta[]>({ queryKey: ['med-ficha', pacienteId, 'recetas'], queryFn: () => medRpc<Receta[]>('med_recetas', { _paciente_id: pacienteId }) });
  const { data: regsFicha = [] } = useQuery<RegFicha[]>({ queryKey: ['med-ficha', pacienteId, 'examenes', 'resumen'], queryFn: () => medRpc<RegFicha[]>('med_examenes_ficha', { _paciente_id: pacienteId, _categoria: null }) });
  const { data: certificados = [] } = useQuery<Certificado[]>({ queryKey: ['med-ficha', pacienteId, 'certificados'], queryFn: () => medRpc<Certificado[]>('med_certificados', { _paciente_id: pacienteId }) });
  const { data: citas } = useQuery<Citas>({ queryKey: ['med-ficha', pacienteId, 'citas'], queryFn: () => medRpc<Citas>('med_paciente_citas', { _paciente_id: pacienteId }) });
  const { data: ant } = useQuery<AntecedentesData>({ queryKey: ['med-ficha', pacienteId, 'antecedentes'], queryFn: () => medRpc<AntecedentesData>('med_antecedentes', { _paciente_id: pacienteId }) });

  const guardarFoto = useMutation({
    mutationFn: (foto: string) => medRpc('med_paciente_foto', { _id: pacienteId, _foto: foto }),
    onSuccess: (_d, foto) => {
      toast.success(foto ? 'Foto actualizada' : 'Foto eliminada');
      void qc.invalidateQueries({ queryKey: ['med-ficha', pacienteId] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const subirFoto = (file: File) => {
    if (!file.type.startsWith('image/')) { toast.error('El archivo debe ser una imagen'); return; }
    if (file.size > 512 * 1024) { toast.error('La imagen supera 512 KB; usa una más liviana'); return; }
    const r = new FileReader();
    r.onload = () => guardarFoto.mutate(String(r.result));
    r.onerror = () => toast.error('No se pudo leer el archivo');
    r.readAsDataURL(file);
  };

  // Últimos signos vitales (aplanados desde las evoluciones, más recientes primero)
  const signos = useMemo(() => (his?.evoluciones ?? [])
    .flatMap((e) => e.signos ?? [])
    .sort((a, b) => (a.fecha < b.fecha ? 1 : -1))
    .slice(0, 10), [his]);

  if (isLoading || !det) {
    return (
      <div className="p-6">
        <Button variant="ghost" size="sm" onClick={onVolver}><ArrowLeft className="w-4 h-4 mr-1" /> Volver</Button>
        <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>
      </div>
    );
  }

  const p = det.paciente; const par = det.partner;
  const genero = GENEROS.find((g) => g.v === p.genero)?.l ?? '—';
  const evoluciones = his?.evoluciones ?? [];
  const vacunas = his?.vacunas ?? [];
  const consultasRaiz = evoluciones.filter((e) => !e.evolucion.episodio_id);
  const pedidosFicha = regsFicha.filter((r) => r.categoria === 'pedido');
  const procedimientosFicha = regsFicha.filter((r) => r.categoria === 'procedimiento');
  const dsx = (d: Record<string, unknown>, k: string): string => (typeof d[k] === 'string' ? (d[k] as string) : '');
  const dxTexto = (dx?: { cie10_codigo: string | null; descripcion: string | null }[] | null) =>
    (dx ?? []).map((x) => `${x.cie10_codigo ? `${x.cie10_codigo} ` : ''}${x.descripcion ?? ''}`.trim()).filter(Boolean);

  // Columna "Tratamiento" del resumen (reliv): plan + receta del mismo día
  // (alergias, PRESCRIPCIÓN numerada, INDICACIONES y RECOMENDACIONES)
  const tratamientoDe = (ev: Evolucion): string => {
    const partes: string[] = [];
    if (ev.evolucion.plan?.trim()) partes.push(ev.evolucion.plan.trim());
    const dia = ev.evolucion.fecha.slice(0, 10);
    recetas.filter((r) => r.fecha.slice(0, 10) === dia && r.estado !== 'anulada').forEach((r) => {
      const d = (r.datos ?? {}) as Record<string, unknown>;
      const items = r.items_detalle ?? [];
      const alergias = dsx(d, 'alergias_farmacologicas');
      partes.push(`ALERGIAS:\n${alergias || 'No refiere alergias farmacológicas'}`);
      if (items.length) {
        partes.push('PRESCRIPCIÓN:\n' + items.map((it, i) =>
          `${i + 1}. ${it.medicamento}${it.comercial ? ` (${it.comercial})` : ''}${it.concentracion ? ` ${it.concentracion}` : ''}${it.via ? `, via ${it.via}` : ''}${it.forma ? `, ${it.forma}` : ''}${it.cantidad ? ` # ${it.cantidad}` : ''}.`).join('\n'));
        const conInd = items.filter((it) => it.indicaciones?.trim());
        if (conInd.length) {
          partes.push('INDICACIONES:\n' + conInd.map((it, i) =>
            `${i + 1}. ${it.medicamento}${it.via ? `, via ${it.via}` : ''}. ${it.indicaciones}`).join('\n'));
        }
      }
      if (dsx(d, 'recomendaciones').trim()) partes.push(`RECOMENDACIONES NO FARMACOLOGICAS:\n${dsx(d, 'recomendaciones')}`);
    });
    return partes.join('\n');
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onVolver}><ArrowLeft className="w-4 h-4 mr-1" /> Volver</Button>
        <h1 className="text-xl font-semibold">Ficha del paciente</h1>
      </div>

      <div className="flex gap-4 items-start">
        {/* Sidebar de secciones */}
        <nav className="hidden md:block w-44 shrink-0 sticky top-4 rounded-md border bg-card p-1.5 space-y-0.5">
          {/* Resumen Clínico: la vista por defecto de la ficha */}
          <button type="button" onClick={() => { setVista('ficha'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted hover:text-foreground ${vista === 'ficha' ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
            <LayoutDashboard className="w-4 h-4 shrink-0" /> Resumen Clínico
          </button>
          {/* Antecedentes: submenú Generales / Pediátricos */}
          <div>
            <button type="button" onClick={() => setMenuAnt((o) => !o)}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground">
              <ClipboardList className="w-4 h-4 shrink-0" /> Antecedentes
              {menuAnt ? <ChevronDown className="w-3.5 h-3.5 ml-auto" /> : <ChevronRight className="w-3.5 h-3.5 ml-auto" />}
            </button>
            {menuAnt && (
              <div className="ml-6 space-y-0.5">
                <button type="button" onClick={() => setVista('general')}
                  className={`block w-full rounded px-2 py-1 text-left text-sm hover:bg-muted ${vista === 'general' ? 'text-primary font-medium' : 'text-muted-foreground hover:text-foreground'}`}>
                  Generales
                </button>
                {/* Coherencia por sexo (691): sin antecedentes gineco-obstétricos en pacientes masculinos */}
                {p.genero !== 'M' && (
                  <button type="button" onClick={() => setVista('gineco')}
                    className={`block w-full rounded px-2 py-1 text-left text-sm hover:bg-muted ${vista === 'gineco' ? 'text-primary font-medium' : 'text-muted-foreground hover:text-foreground'}`}>
                    Gineco-Obstétricos
                  </button>
                )}
                <button type="button" onClick={() => setVista('pediatrico')}
                  className={`block w-full rounded px-2 py-1 text-left text-sm hover:bg-muted ${vista === 'pediatrico' ? 'text-primary font-medium' : 'text-muted-foreground hover:text-foreground'}`}>
                  Pediátricos
                </button>
              </div>
            )}
          </div>
          {/* Enfermedad Actual: consultas del paciente */}
          <button type="button" onClick={() => setVista('enfermedad')}
            className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted hover:text-foreground ${vista === 'enfermedad' ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
            <HeartPulse className="w-4 h-4 shrink-0" /> Enfermedad Actual
          </button>
          {/* Exámenes: submenú Laboratorio / Estudios Imagen / Por Especialidad / Patología */}
          <div>
            <button type="button" onClick={() => setMenuEx((o) => !o)}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground">
              <Microscope className="w-4 h-4 shrink-0" /> Exámenes
              {menuEx ? <ChevronDown className="w-3.5 h-3.5 ml-auto" /> : <ChevronRight className="w-3.5 h-3.5 ml-auto" />}
            </button>
            {menuEx && (
              <div className="ml-6 space-y-0.5">
                {([['ex_lab', 'Laboratorio'], ['ex_img', 'Estudios Imagen'], ['ex_esp', 'Por Especialidad'], ['ex_pat', 'Patología']] as const).map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setVista(v)}
                    className={`block w-full rounded px-2 py-1 text-left text-sm hover:bg-muted ${vista === v ? 'text-primary font-medium' : 'text-muted-foreground hover:text-foreground'}`}>
                    {l}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Formularios de aseguradora (genérico) */}
          <button type="button" onClick={() => setVista('formularios')}
            className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted hover:text-foreground ${vista === 'formularios' ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
            <FileText className="w-4 h-4 shrink-0" /> Formularios
          </button>
          {/* Reclamos de aseguradora (auto-llenado del PDF oficial) */}
          <button type="button" onClick={() => setVista('reclamos')}
            className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted hover:text-foreground ${vista === 'reclamos' ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
            <FileText className="w-4 h-4 shrink-0" /> Reclamos aseguradora
          </button>
          {/* Documentos: submenú de 8 opciones (reliv) */}
          <div>
            <button type="button" onClick={() => setMenuDoc((o) => !o)}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground">
              <FolderOpen className="w-4 h-4 shrink-0" /> Documentos
              {menuDoc ? <ChevronDown className="w-3.5 h-3.5 ml-auto" /> : <ChevronRight className="w-3.5 h-3.5 ml-auto" />}
            </button>
            {menuDoc && (
              <div className="ml-6 space-y-0.5">
                {([['doc_adj', 'Archivos Adjuntos'], ['doc_inf', 'Informes'], ['doc_con', 'Consentimiento Informado'], ['doc_ord', 'Orden Ingreso'],
                  ['doc_cert', 'Certificados'], ['doc_int', 'Interconsulta'], ['doc_pre', 'Chequeo Preoperatorio'], ['doc_pro', 'Protocolo Operatorio']] as const).map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setVista(v)}
                    className={`block w-full rounded px-2 py-1 text-left text-sm hover:bg-muted ${vista === v ? 'text-primary font-medium' : 'text-muted-foreground hover:text-foreground'}`}>
                    {l}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Procedimientos */}
          <button type="button" onClick={() => setVista('procedimientos')}
            className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted hover:text-foreground ${vista === 'procedimientos' ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
            <Scissors className="w-4 h-4 shrink-0" /> Procedimientos
          </button>
          {/* Cobros/Cargos */}
          <button type="button" onClick={() => setVista('cobros')}
            className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted hover:text-foreground ${vista === 'cobros' ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
            <BadgeDollarSign className="w-4 h-4 shrink-0" /> Cobros/Cargos
          </button>
          {/* Historia Clínica (vista HCE compartida con Clínica → Historia Clínica; Imprimir = logos y firmas) */}
          <button type="button" onClick={() => setVista('historia')}
            className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted hover:text-foreground ${vista === 'historia' ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
            <BookOpen className="w-4 h-4 shrink-0" /> Historia Clínica
          </button>
          {SECCIONES.map(([k, l, Icono]) => (
            <button key={k} type="button" onClick={() => (k === 'vacunas' ? setVista('vacunas') : k === 'recetas' ? setVista('recetas_ficha') : k === 'pedidos' ? setVista('pedidos_ficha') : irSeccion(k))}
              className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted hover:text-foreground ${(k === 'vacunas' && vista === 'vacunas') || (k === 'recetas' && vista === 'recetas_ficha') || (k === 'pedidos' && vista === 'pedidos_ficha') ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
              <Icono className="w-4 h-4 shrink-0" /> {l}
            </button>
          ))}
        </nav>

        <div className="flex-1 min-w-0 space-y-4">
          {/* Cabecera demográfica */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex flex-col items-center gap-1">
                  <div className="relative">
                    {p.foto_url
                      ? <img src={p.foto_url} alt={par.razon_social} className="w-24 h-24 rounded-full object-cover border" />
                      : <div className="flex w-24 h-24 items-center justify-center rounded-full bg-primary/15 text-3xl font-semibold text-primary">{iniciales(par.razon_social)}</div>}
                    <label className="absolute -bottom-1 -right-1 flex w-8 h-8 cursor-pointer items-center justify-center rounded-full border bg-background shadow hover:bg-muted" title="Cambiar foto">
                      {guardarFoto.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) subirFoto(f); e.target.value = ''; }} />
                    </label>
                  </div>
                  {p.foto_url && (
                    <button type="button" className="inline-flex items-center gap-1 text-xs text-red-500 hover:underline" onClick={() => guardarFoto.mutate('')}>
                      <Trash2 className="w-3 h-3" /> Quitar foto
                    </button>
                  )}
                </div>

                <div className="flex-1 min-w-[280px] space-y-3">
                  <div className="flex items-center justify-between rounded-md bg-primary/10 px-3 py-2">
                    <p className="font-semibold">
                      {par.razon_social}
                      {p.codigo && <span className="ml-1 text-muted-foreground font-normal">(HC {p.codigo})</span>}
                      {par.identificacion && <span className="ml-1 text-muted-foreground font-normal">— {par.identificacion}</span>}
                      {!p.es_activo && <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-300">Inactivo</span>}
                    </p>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-background/60" title="Editar paciente" aria-label="Editar paciente" onClick={() => onEditar(pacienteId)}>
                      <Pencil className="w-4 h-4" />
                    </button>
                  </div>
                  {det?.centro_costo?.nombre && (
                    <p>
                      <span className="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-2.5 py-1 text-sm font-bold text-primary">
                        <span className="text-[10px] font-semibold uppercase tracking-wide opacity-70">Misión</span>
                        {det.centro_costo.codigo ? `${det.centro_costo.codigo} · ` : ''}{det.centro_costo.nombre}
                      </span>
                    </p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5 text-sm">
                    <p><b>Sexo:</b> {genero}</p>
                    <p><b>E-mail:</b> {par.email || '—'}</p>
                    <p><b>Identificación:</b> {par.identificacion || '—'}</p>
                    <p><b>Fecha nacimiento:</b> {fmtF(p.fecha_nacimiento)}</p>
                    <p><b>Celular:</b> {par.celular || '—'}</p>
                    <p><b>Fecha de registro:</b> {fmtF(p.fecha_registro)}</p>
                    <p><b>Edad:</b> {calcEdad(p.fecha_nacimiento)}</p>
                    <p><b>Teléfono:</b> {par.telefono || '—'}</p>
                    <p><b>Seguro médico:</b> {p.aseguradora ? `${p.aseguradora}${p.numero_poliza ? ` · ${p.numero_poliza}` : ''}` : '—'}</p>
                    <p><b>Nacionalidad:</b> {par.nacionalidad || '—'}</p>
                    <p><b>Tipo de sangre:</b> {p.tipo_sangre || '—'}</p>
                    <p><b>Ciudad:</b> {par.ciudad || '—'}</p>
                    <p><b>País:</b> {par.pais || '—'}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {vista === 'general' && <AntecedentesGenerales pacienteId={pacienteId} nombre={par.razon_social} onVolver={() => setVista('ficha')} />}
          {vista === 'gineco' && p.genero !== 'M' && <AntecedentesGenerales pacienteId={pacienteId} nombre={par.razon_social} focoGineco onVolver={() => setVista('ficha')} />}
          {vista === 'pediatrico' && <AntecedentesPediatricos pacienteId={pacienteId} nombre={par.razon_social} onVolver={() => setVista('ficha')} />}
          {vista === 'historia' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">Historia Clínica</h2>
                <Button size="sm" variant="outline" onClick={() => setVista('ficha')}><ArrowLeft className="w-4 h-4 mr-1" /> Volver</Button>
              </div>
              <HistoriaClinicaPaciente pacienteId={pacienteId} soloLectura />
            </div>
          )}
          {vista === 'vacunas' && (
            <EsquemaVacunacion pacienteId={pacienteId} nombre={par.razon_social}
              fechaNacimiento={p.fecha_nacimiento} onVolver={() => setVista('ficha')} />
          )}
          {vista === 'enfermedad' && (
            <EnfermedadActual pacienteId={pacienteId} nombre={par.razon_social}
              fechaNacimiento={p.fecha_nacimiento} genero={p.genero} onVolver={() => setVista('ficha')} />
          )}
          {vista === 'ex_lab' && <ExamenesLaboratorio pacienteId={pacienteId} onVolver={() => setVista('ficha')} />}
          {vista === 'ex_img' && <ExamenesImagen pacienteId={pacienteId} onVolver={() => setVista('ficha')} />}
          {vista === 'ex_esp' && <ExamenesEspecialidad pacienteId={pacienteId} nombre={par.razon_social} mision={det?.centro_costo ?? null} onVolver={() => setVista('ficha')} />}
          {vista === 'ex_pat' && <ExamenesPatologia pacienteId={pacienteId} nombre={par.razon_social} mision={det?.centro_costo ?? null} onVolver={() => setVista('ficha')} />}
          {vista === 'formularios' && (
            <FormulariosPaciente pacienteId={pacienteId} nombre={par.razon_social} identificacion={par.identificacion}
              aseguradora={p.aseguradora} poliza={p.numero_poliza} onVolver={() => setVista('ficha')} />
          )}
          {vista === 'reclamos' && (
            <ReclamoAseguradora pacienteId={pacienteId} onVolver={() => setVista('ficha')} />
          )}
          {vista === 'doc_adj' && <ArchivosAdjuntos pacienteId={pacienteId} onVolver={() => setVista('ficha')} />}
          {vista === 'doc_inf' && <InformesFicha pacienteId={pacienteId} nombre={par.razon_social} onVolver={() => setVista('ficha')} />}
          {vista === 'doc_con' && <ConsentimientoInformado pacienteId={pacienteId} nombre={par.razon_social} identificacion={par.identificacion} onVolver={() => setVista('ficha')} />}
          {vista === 'doc_ord' && <OrdenIngreso pacienteId={pacienteId} nombre={par.razon_social} mision={det?.centro_costo ?? null} onVolver={() => setVista('ficha')} />}
          {vista === 'doc_cert' && <CertificadosFicha pacienteId={pacienteId} onVolver={() => setVista('ficha')} />}
          {vista === 'doc_int' && <InterconsultaFicha pacienteId={pacienteId} nombre={par.razon_social} mision={det?.centro_costo ?? null} onVolver={() => setVista('ficha')} />}
          {vista === 'doc_pre' && <ChequeoPreoperatorio pacienteId={pacienteId} nombre={par.razon_social} mision={det?.centro_costo ?? null} onVolver={() => setVista('ficha')} />}
          {vista === 'doc_pro' && <ProtocoloOperatorio pacienteId={pacienteId} nombre={par.razon_social} mision={det?.centro_costo ?? null} onVolver={() => setVista('ficha')} />}
          {vista === 'procedimientos' && <ProcedimientosFicha pacienteId={pacienteId} nombre={par.razon_social} mision={det?.centro_costo ?? null} onVolver={() => setVista('ficha')} />}
          {vista === 'recetas_ficha' && <RecetasFicha pacienteId={pacienteId} onVolver={() => setVista('ficha')} />}
          {vista === 'pedidos_ficha' && <PedidosFicha pacienteId={pacienteId} nombre={par.razon_social} genero={p.genero} mision={det?.centro_costo ?? null} onVolver={() => setVista('ficha')} />}
          {vista === 'cobros' && <CobrosFicha pacienteId={pacienteId} nombre={par.razon_social} onVolver={() => setVista('ficha')} />}

          {vista === 'ficha' && (<>
          <Seccion titulo="Antecedentes" innerRef={(el) => { refs.current.antecedentes = el; }}>
            <table className="w-full text-sm">
              <thead><tr className="border-b"><TH>Antecedentes</TH><TH>Categoría</TH><TH>Descripción</TH><TH>{''}</TH></tr></thead>
              <tbody>
                {(() => {
                  const cap = (k: string) => k.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
                  // Subcategorías del bloque Personales (estilo reliv)
                  const subPersonal = (row: AntecedenteRow | null): [string, string][] => {
                    if (!row) return [];
                    const d = row.datos ?? {};
                    const filas: [string, string][] = [];
                    if (row.sin_patologia) filas.push(['Patologías', 'SIN PATOLOGÍA APARENTE']);
                    else {
                      const res = resumenAntecedente(row);
                      if (res && res !== 'Registrado (ver detalle)') filas.push(['Patologías', res]);
                    }
                    const hab = (d.habitos ?? {}) as Record<string, string>;
                    if (Object.keys(hab).length) filas.push(['Hábitos', Object.entries(hab).map(([k, v]) => `${cap(k)}${v?.trim() ? ` (${v.trim()})` : ''}`).join(' · ')]);
                    const gin = (d.gineco ?? {}) as Record<string, string>;
                    const ginTxt = Object.entries(gin).filter(([, v]) => v?.trim()).map(([k, v]) => `${cap(k)}: ${v.trim()}`).join(' · ');
                    if (ginTxt) filas.push(['Gineco-Obstétricos', ginTxt]);
                    ([['medicamentos', 'Medicamentos'], ['otros', 'Otros Antecedentes Patológicos'], ['cirugias', 'Cirugías'],
                      ['alergias', 'Alergias y Alertas'], ['alergias_farmacologicas', 'Alergias Farmacológicas'],
                      ['perinatales', 'Perinatales'], ['comentarios', 'Comentarios']] as const).forEach(([k, l]) => {
                      const v = typeof d[k] === 'string' ? (d[k] as string) : '';
                      if (v.trim()) filas.push([l, v.trim()]);
                    });
                    return filas;
                  };
                  const editarBtn = (v: 'general' | 'pediatrico') => (
                    <button type="button" className="text-xs text-primary hover:underline" onClick={() => { setMenuAnt(true); setVista(v); }}>Editar</button>
                  );
                  const vacioBtn = (txt: string, v: 'general' | 'pediatrico') => (
                    <button type="button" className="text-primary/80 hover:underline text-left" onClick={() => { setMenuAnt(true); setVista(v); }}>{txt}</button>
                  );
                  const fam = resumenAntecedente(ant?.familiar ?? null);
                  const per = subPersonal(ant?.personal ?? null);
                  const ped = resumenAntecedente(ant?.pediatrico ?? null);
                  return (<>
                    <tr className="border-b">
                      <TD className="font-medium whitespace-nowrap">Familiares</TD>
                      <TD>{''}</TD>
                      <TD>{fam ?? vacioBtn('No existen antecedentes familiares registrados', 'general')}</TD>
                      <TD className="text-right">{editarBtn('general')}</TD>
                    </tr>
                    {per.length === 0 ? (
                      <tr className="border-b">
                        <TD className="font-medium whitespace-nowrap">Personales</TD>
                        <TD>{''}</TD>
                        <TD>{vacioBtn('No existen antecedentes personales registrados', 'general')}</TD>
                        <TD className="text-right">{editarBtn('general')}</TD>
                      </tr>
                    ) : per.map(([cat, valor], i) => (
                      <tr key={cat} className="border-b">
                        <TD className="font-medium whitespace-nowrap">{i === 0 ? 'Personales' : ''}</TD>
                        <TD className="whitespace-nowrap">{cat}</TD>
                        <TD className="whitespace-pre-wrap">{valor}</TD>
                        <TD className="text-right">{i === 0 ? editarBtn('general') : ''}</TD>
                      </tr>
                    ))}
                    <tr className="border-b">
                      <TD className="font-medium whitespace-nowrap">Pediatría</TD>
                      <TD>{''}</TD>
                      <TD>{ped ?? vacioBtn('No existen antecedentes pediátricos registrados', 'pediatrico')}</TD>
                      <TD className="text-right">{editarBtn('pediatrico')}</TD>
                    </tr>
                    {([['Notas', p.antecedentes], ['Alergias', p.alergias], ['Medicamentos actuales', p.medicamentos_actuales], ['Observaciones', p.observaciones]] as const)
                      .filter(([, val]) => val)
                      .map(([l, val]) => <tr key={l} className="border-b last:border-0"><TD className="font-medium whitespace-nowrap">{l}</TD><TD>{''}</TD><TD>{val}</TD><TD>{''}</TD></tr>)}
                  </>);
                })()}
              </tbody>
            </table>
          </Seccion>

          <Seccion titulo="Consultas" innerRef={(el) => { refs.current.consultas = el; }}>
            {consultasRaiz.length === 0 ? <Vacio texto="No existen consultas registradas" /> : (
              <table className="w-full text-sm">
                <thead><tr className="border-b"><TH>Fecha</TH><TH>CIE-10</TH><TH>Tratamiento</TH><TH># Evolución</TH></tr></thead>
                <tbody>
                  {consultasRaiz.map((e) => {
                    const nEvo = evoluciones.filter((h) => h.evolucion.episodio_id === e.evolucion.id).length;
                    const trat = tratamientoDe(e);
                    return (
                      <tr key={e.evolucion.id} className="border-b last:border-0">
                        <TD className="whitespace-nowrap">{fmtF(e.evolucion.fecha)}<div className="text-xs text-muted-foreground">{e.medico}</div></TD>
                        <TD className="max-w-[220px]">{dxTexto(e.diagnosticos).map((t, i) => <div key={i}>{t}</div>)}{!e.diagnosticos?.length && '—'}</TD>
                        <TD className="whitespace-pre-wrap text-xs">{trat || '—'}</TD>
                        <TD className="text-center">{nEvo}</TD>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Seccion>

          <Seccion titulo="Recetas" innerRef={(el) => { refs.current.recetas = el; }}>
            {recetas.length === 0 ? <Vacio texto="No existen Recetas registradas" /> : (
              <table className="w-full text-sm">
                <thead><tr className="border-b"><TH>Fecha</TH><TH>Diagnóstico</TH><TH>Indicaciones</TH><TH>Estado</TH></tr></thead>
                <tbody>
                  {recetas.map((r) => (
                    <tr key={r.id} className="border-b last:border-0">
                      <TD className="whitespace-nowrap">{fmtF(r.fecha)}<div className="text-xs text-muted-foreground font-mono">{r.codigo ?? ''}</div></TD>
                      <TD>{[...dxTexto((r.datos?.diagnosticos as { cie10_codigo: string | null; descripcion: string | null }[] | undefined) ?? []), r.diagnostico || ''].filter(Boolean).map((t, i) => <div key={i}>{t}</div>)}</TD>
                      <TD>{(r.items_detalle ?? []).map((it, i) => <div key={i} className="font-medium">{it.medicamento}</div>)}{!(r.items_detalle ?? []).length && r.items}</TD>
                      <TD><EstadoBadge v={r.estado} /></TD>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Seccion>

          <Seccion titulo="Pedidos" innerRef={(el) => { refs.current.pedidos = el; }}>
            {pedidosFicha.length === 0 ? <Vacio texto="No existen Pedidos registrados" /> : (
              <table className="w-full text-sm">
                <thead><tr className="border-b"><TH>Fecha</TH><TH>Tipo pedido</TH><TH>Diagnósticos</TH></tr></thead>
                <tbody>
                  {pedidosFicha.map((r) => (
                    <tr key={r.id} className="border-b last:border-0">
                      <TD className="whitespace-nowrap">{fmtF(r.fecha)}</TD>
                      <TD>{dsx(r.datos, 'tipo_pedido') || '—'}</TD>
                      <TD>{[...dxTexto((r.datos.diagnosticos as { cie10_codigo: string | null; descripcion: string | null }[] | undefined) ?? []), dsx(r.datos, 'diagnostico')].filter(Boolean).map((t, i) => <div key={i}>{t}</div>)}</TD>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Seccion>

          <Seccion titulo="Procedimientos" innerRef={(el) => { refs.current.procs = el; }}>
            {procedimientosFicha.length === 0 ? <Vacio texto="No existen Procedimientos registrados" /> : (
              <table className="w-full text-sm">
                <thead><tr className="border-b"><TH>Fecha</TH><TH>Procedimiento</TH><TH>Descripción del Procedimiento</TH></tr></thead>
                <tbody>
                  {procedimientosFicha.map((r) => (
                    <tr key={r.id} className="border-b last:border-0">
                      <TD className="whitespace-nowrap">{fmtF(r.fecha)}</TD>
                      <TD className="font-medium">{dsx(r.datos, 'procedimiento') || '—'}</TD>
                      <TD className="whitespace-pre-wrap">{dsx(r.datos, 'descripcion') || '—'}</TD>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Seccion>

          <Seccion titulo="Certificados" innerRef={(el) => { refs.current.certificados = el; }}>
            {certificados.length === 0 ? <Vacio texto="No existen certificados registrados" /> : (
              <table className="w-full text-sm">
                <thead><tr className="border-b"><TH>Código</TH><TH>Fecha</TH><TH>Tipo</TH><TH>Médico</TH><TH>Diagnóstico</TH><TH>Reposo</TH><TH>Estado</TH></tr></thead>
                <tbody>
                  {certificados.map((c) => (
                    <tr key={c.id} className="border-b last:border-0">
                      <TD className="font-mono whitespace-nowrap">{c.codigo ?? '—'}</TD>
                      <TD className="whitespace-nowrap">{fmtF(c.fecha)}</TD>
                      <TD className="capitalize">{c.tipo ?? '—'}</TD>
                      <TD>{c.medico}</TD>
                      <TD>{c.diagnostico || '—'}</TD>
                      <TD>{c.dias_reposo ? `${c.dias_reposo} días` : '—'}</TD>
                      <TD><EstadoBadge v={c.estado} /></TD>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Seccion>

          <Seccion titulo="Citas" innerRef={(el) => { refs.current.citas = el; }}>
            {citas?.proxima && (
              <p className="mb-2 rounded-md bg-primary/10 px-3 py-2 text-sm">
                <b>Próxima cita:</b> {fmtF(citas.proxima.fecha)} {hora(citas.proxima.hora_inicio)} · {citas.proxima.medico}
                {citas.proxima.especialidad && <> · {citas.proxima.especialidad}</>} <EstadoBadge v={citas.proxima.estado} />
              </p>
            )}
            {!citas || citas.citas.length === 0 ? <Vacio texto="No existen citas registradas" /> : (
              <table className="w-full text-sm">
                <thead><tr className="border-b"><TH>Fecha</TH><TH>Hora</TH><TH>Médico</TH><TH>Especialidad</TH><TH>Tipo</TH><TH>Consultorio</TH><TH>Estado</TH></tr></thead>
                <tbody>
                  {citas.citas.map((c) => (
                    <tr key={c.id} className="border-b last:border-0">
                      <TD className="whitespace-nowrap">{fmtF(c.fecha)}</TD>
                      <TD className="whitespace-nowrap">{hora(c.hora_inicio)}{c.hora_fin ? `–${hora(c.hora_fin)}` : ''}</TD>
                      <TD>{c.medico}</TD>
                      <TD>{c.especialidad ?? '—'}</TD>
                      <TD className="capitalize">{c.tipo_atencion ?? '—'}</TD>
                      <TD>{c.consultorio ?? '—'}</TD>
                      <TD><EstadoBadge v={c.estado} /></TD>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Seccion>

          <Seccion titulo="Vacunas" innerRef={(el) => { refs.current.vacunas = el; }}>
            <div className="mb-1 text-right">
              <button type="button" className="text-xs text-primary hover:underline" onClick={() => setVista('vacunas')}>Gestionar esquema de vacunación</button>
            </div>
            {vacunas.length === 0 ? (
              <button type="button" className="py-1 text-sm text-primary/80 hover:underline" onClick={() => setVista('vacunas')}>No existen vacunas registradas</button>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="border-b"><TH>Vacuna</TH><TH>Dosis</TH><TH>Fecha</TH><TH>Vía</TH><TH>Próxima dosis</TH></tr></thead>
                <tbody>
                  {vacunas.map((v) => (
                    <tr key={v.id} className="border-b last:border-0">
                      <TD>{v.vacuna}</TD>
                      <TD>{v.dosis ?? '—'}</TD>
                      <TD className="whitespace-nowrap">{fmtF(v.fecha)}</TD>
                      <TD className="capitalize">{v.via ?? '—'}</TD>
                      <TD className="whitespace-nowrap">{fmtF(v.proxima_fecha)}</TD>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Seccion>

          <Seccion titulo="Signos vitales (últimos 10)" innerRef={(el) => { refs.current.signos = el; }}>
            {signos.length === 0 ? <Vacio texto="No existen signos vitales registrados" /> : (
              <table className="w-full text-sm">
                <thead><tr className="border-b"><TH>Fecha</TH><TH>PA</TH><TH>FC</TH><TH>FR</TH><TH>Temp</TH><TH>SpO2</TH><TH>Peso</TH><TH>Talla</TH><TH>Glucosa</TH></tr></thead>
                <tbody>
                  {signos.map((s, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <TD className="whitespace-nowrap">{fmtF(s.fecha)}</TD>
                      <TD>{s.presion_sistolica != null ? `${s.presion_sistolica}/${s.presion_diastolica ?? '—'}` : '—'}</TD>
                      <TD>{s.frecuencia_cardiaca ?? '—'}</TD>
                      <TD>{s.frecuencia_resp ?? '—'}</TD>
                      <TD>{s.temperatura != null ? `${s.temperatura} °C` : '—'}</TD>
                      <TD>{s.spo2 != null ? `${s.spo2}%` : '—'}</TD>
                      <TD>{s.peso != null ? `${s.peso} kg` : '—'}</TD>
                      <TD>{s.talla != null ? `${s.talla} cm` : '—'}</TD>
                      <TD>{s.glucosa ?? '—'}</TD>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Seccion>

          <Seccion titulo="Documentos" innerRef={(el) => { refs.current.documentos = el; }}>
            {det.documentos.length === 0 ? <Vacio texto="No existen documentos registrados" /> : (
              <table className="w-full text-sm">
                <thead><tr className="border-b"><TH>Nombre</TH><TH>Tipo</TH><TH>Fecha</TH><TH>Archivo</TH></tr></thead>
                <tbody>
                  {det.documentos.map((d) => (<Fragment key={d.id}>
                    <tr className="border-b last:border-0">
                      <TD>{d.nombre}</TD>
                      <TD className="capitalize">{d.tipo ?? '—'}</TD>
                      <TD className="whitespace-nowrap">{fmtF(d.fecha)}</TD>
                      <TD>{d.archivo_url ? <button type="button" className="text-primary hover:underline" onClick={() => setDocVisto(docVisto === d.id ? null : d.id)}>{docVisto === d.id ? 'Ocultar' : 'Ver'}</button> : '—'}</TD>
                    </tr>
                    {docVisto === d.id && d.archivo_url && (
                      <tr className="border-b"><td colSpan={4} className="py-2">
                        <VisorArchivo a={{ nombre: d.nombre, mime: (d as { mime?: string | null }).mime ?? null, archivo: d.archivo_url }} onClose={() => setDocVisto(null)} />
                      </td></tr>
                    )}
                  </Fragment>))}
                </tbody>
              </table>
            )}
          </Seccion>
          </>)}
        </div>
      </div>
    </div>
  );
}
